import { CompanyLicenseInformation } from '../../types/CompanyLicenseInformation';
import { CompanyActionContext } from 'apps/webcomponents/src/company/common/types/CompanyActionContext';
import { CompanyType } from '../../../company/types/company-type';
import { LicenseType } from '../../types/LicenseType';
import _ from 'lodash';

export class LicenseFormHelper {
  public static isTrialAvailable(
    licenseInfo: CompanyLicenseInformation,
    context: CompanyActionContext,
    canManageNetworks?: boolean
  ) {
    // we do not have trial option for private cloud
    if (Application.Main.product.isPrivateCloud()) {
      return false;
    }

    // we do not allow this license type if Manage Networks is not checked
    if (canManageNetworks === false) {
      return false;
    }

    // we only have one type of trial (licenseType=1), so if a company has TRIAL display TRIAL
    if (licenseInfo.editedCompany.licenseType === LicenseType.Trial) {
      return true;
    }

    // check common conditions regarding the main company (both for creating and updating another company)
    if (
      licenseInfo.hasFratSubscription ||
      CompanyLicenseInformation.hasFratEndUser(licenseInfo) ||
      licenseInfo.parentHasMspTrialLicense ||
      licenseInfo.parentHasBasicMonthlySubscription
    ) {
      return false;
    }

    // if previous conditions are fulfilled, the case when creating a new company is covered
    if (context === CompanyActionContext.Create) {
      return true;
    }

    // if we got this far, it means that we do not have to display the TRIAL option.
    return false;
  }

  public static isFratAvailable(
    licenseInfo: CompanyLicenseInformation,
    context: CompanyActionContext,
    canManageNetworks?: boolean
  ) {
    // we do not allow this license type if Manage Networks is not checked
    if (canManageNetworks === false) {
      return false;
    }

    // To display the FRAT Subscription option in the dropdown when a company is created
    // parent.feature.fratManager === true
    // hasFratSubscription must have one of the values below:
    // - true, in case the creator company has a FRAT Subscription
    // - null, in case the creator company has been selected to provide FRAT Subscription (e.g. by DevOps)
    if (context === CompanyActionContext.Create) {
      return licenseInfo.hasFratSubscription !== false && !!licenseInfo.parentIsFratManager;
    }

    return !!licenseInfo.hasFratSubscription;
  }

  public static isPrePaidLicenseAvailable(licenseInfo: CompanyLicenseInformation, context: CompanyActionContext) {
    const isOwnCompany = context === CompanyActionContext.MyCompany;

    // check for case when creating a new company
    if (context === CompanyActionContext.Create) {
      return !licenseInfo.parentHasMspTrialLicense && !licenseInfo.hasFratSubscription;
    }

    // check for case when updating an existing company(My Company case)
    // hide license option for partners having frat subscription editing their own company details
    if (isOwnCompany && licenseInfo.hasFratSubscription) {
      return false;
    }

    // hide license option for partners having frat subscription editing any other company
    if (licenseInfo.parentHasFratSubscription) {
      return false;
    }

    return licenseInfo.editedCompany.licenseType !== LicenseType.Monthly || !licenseInfo.parentHasMspTrialLicense;
  }

  public static isBasicMonthlySubscriptionAvailable(
    licenseInfo: CompanyLicenseInformation,
    context: CompanyActionContext
  ) {
    // case when creating new company
    if (context === CompanyActionContext.Create) {
      return !!(licenseInfo.parentHasBasicMonthlySubscription || licenseInfo.parentIsBasicSecurityProvider);
    }

    // case when updating my company
    if (context === CompanyActionContext.MyCompany) {
      return licenseInfo.hasBasicMonthlySubscription;
    }

    // case when updating other company
    return !!(
      licenseInfo.hasBasicMonthlySubscription ||
      licenseInfo.parentHasBasicMonthlySubscription ||
      licenseInfo.parentIsBasicSecurityProvider
    );
  }

  public static isMonthlyLicenseTrialAvailable(
    licenseInfo: CompanyLicenseInformation,
    context: CompanyActionContext,
    companyType?: CompanyType,
    canManageNetworks?: boolean
  ) {
    // we do not have trial option for private cloud
    if (Application.Main.product.isPrivateCloud()) {
      return false;
    }

    // we do not allow this license type if Manage Networks is not checked
    if (canManageNetworks === false) {
      return false;
    }

    // check common conditions regarding the main company (both for creating and updating another company)
    if (
      CompanyLicenseInformation.hasFratEndUser(licenseInfo) ||
      licenseInfo.parentHasMspTrialLicense ||
      companyType !== CompanyType.Partner ||
      licenseInfo.parentHasFratSubscription
    ) {
      return false;
    }

    // hide monthly license trial option, for partners having frat subscription, the case when creating a new company
    // hide monthly license  trial option, for partners having frat subscription editing their own company details
    if (
      licenseInfo.hasFratSubscription &&
      _.includes([CompanyActionContext.Create, CompanyActionContext.MyCompany], context)
    ) {
      return false;
    }

    if (context === CompanyActionContext.Create && licenseInfo.licenseType === LicenseType.Trial) {
      return false;
    }

    // show msp trial option, for partners having frat subscription, the case when creating a child company
    if (licenseInfo.hasFratSubscription) {
      return true;
    }

    // if previous conditions are fulfilled, 'Monthly License Trial' option is available at create and edit
    if (context === CompanyActionContext.Create) {
      return true;
    }

    return licenseInfo.editedCompany.licenseType === LicenseType.Monthly && licenseInfo.editedCompany.hasMspTrial;
  }

  public static isMonthlySubscriptionAvailable(
    licenseInfo: CompanyLicenseInformation,
    context: CompanyActionContext
  ): boolean {
    // we do not have MSP option for private cloud
    if (Application.Main.product.isPrivateCloud()) {
      return false;
    }

    const isOwnCompany = context === CompanyActionContext.MyCompany;
    const canInheritClassicMonthlySubscription =
      !isOwnCompany &&
      licenseInfo.hasMonthlyLicense &&
      !licenseInfo.parentHasMspTrialLicense &&
      !licenseInfo.parentHasBasicMonthlySubscription;

    // cases when creating a new company / editing a company are covered
    if (_.includes([CompanyActionContext.Create, CompanyActionContext.Edit], context)) {
      return canInheritClassicMonthlySubscription;
    }

    // - do not allow companies with Monthly Subscription to exist directly under a Basic Monthly Subscription
    if (licenseInfo.parentHasBasicMonthlySubscription) {
      return false;
    }

    return (
      canInheritClassicMonthlySubscription ||
      (licenseInfo.editedCompany.licenseType === LicenseType.Monthly &&
        licenseInfo.editedCompany.inheritedLicense &&
        !licenseInfo.editedCompany.hasMspTrial)
    );
  }

  public static isMonthlySubscriptionTrialAvailable(
    licenseInfo: CompanyLicenseInformation,
    context: CompanyActionContext
  ) {
    // we do not have trial option for private cloud
    if (Application.Main.product.isPrivateCloud()) {
      return false;
    }

    if (!licenseInfo.parentHasMspTrialLicense) {
      return false;
    }

    return _.includes([CompanyActionContext.Create, CompanyActionContext.Edit], context);
  }
}
